import React, { useState } from 'react';
import styled from 'styled-components';

import { Button } from 'src/presentation/components/agent_desktop_components';
import fuzzySearch from 'src/presentation/components/pages/shared/utils/fuzzySearch';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { PhoneNumberCapability } from 'src/actions/availableNumbers/availableNumbersResource';
import { Row } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/components';
import { SearchInput } from 'src/presentation/components/agent_desktop_components';
import {
  StepWizardActionContainer,
  StepWizardField,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import smsConfigurationResource from 'src/actions/channels/smsConfigurationResource';
import TablePhoneNumbers from './TablePhoneNumbers';
import useActivateNumberForSms from 'src/presentation/components/pages/channels/shared/hooks/useActivateNumberForSms';
import useFetchPhoneNumbers from 'src/presentation/components/pages/channels/shared/hooks/useFetchPhoneNumbers';
import { useResetFormState } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import { PhoneNumberStatus } from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';

type PropsType = {
  entityType?: PhoneNumberCapability;
  nextStep?;
  previousStep?;
  saveStep: Function;
  isActive?: boolean;
  stageConfig?;
};

export default function ExistSearchPhoneNumbers({
  saveStep,
  nextStep,
  previousStep,
  entityType,
  isActive,
  stageConfig,
}: PropsType) {
  const { values, isLoading } = useFetchPhoneNumbers(entityType!);
  const activeNumbers = values.filter(number => number.status !== PhoneNumberStatus.DELETED);
  const [items, setItems] = useState(activeNumbers);
  const [searchString, setSearchString] = useState('');

  const { activateNumber, isPersisting } = useActivateNumberForSms({
    add: smsConfigurationResource.effects.add,
    isActive,
    nextStep,
    saveStep,
  });

  const handleInputChange = ({ target: { value: searchString } }) => {
    const keys = ['locality', 'phoneNumber', 'region'];
    const searchHandler = fuzzySearch({ keys });
    setItems(searchHandler(activeNumbers, searchString));
    setSearchString(searchString);
  };

  const handleSelectPhone = selectedPhone => {
    if (entityType === PhoneNumberCapability.SMS) {
      saveStep({ selectedPhone });
      activateNumber(selectedPhone);
      return;
    }
    saveStep({ selectedPhone });
    nextStep();
  };

  useResetFormState(
    null,
    () => {
      handleInputChange({ target: { value: '' } });
    },
    stageConfig,
    entityType
  );

  const typeString = entityType === PhoneNumberCapability.SMS ? 'SMS' : 'voice';

  return (
    <>
      <StepWizardField>
        <H3 data-aid="wizard-title">Search existing Gladly phone numbers</H3>
        <StyledStepDescription>
          You have {items.length} number(s) that can be configured for {typeString}.
        </StyledStepDescription>
        <StyledRow>
          <StyledSearchInput data-aid="searchInput" onChange={handleInputChange} placeholder="Search numbers" />
        </StyledRow>
      </StepWizardField>
      <TablePhoneNumbers
        handleSelectPhone={handleSelectPhone}
        items={items}
        isLoading={isLoading}
        searchString={searchString}
        isPersisting={isPersisting}
      />
      <StyledStepWizardActionContainer>
        <Button data-aid="editor-cancel" type={Button.Types.SECONDARY} onClick={previousStep}>
          Back
        </Button>
      </StyledStepWizardActionContainer>
    </>
  );
}

const StyledStepDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.insetMedium};
  line-height: 162%;
  margin-bottom: ${({ theme }) => theme.spacing.insetLarge};
  text-align: left;
`;
const StyledRow = styled(Row)`
  width: inherit;
  margin-bottom: ${({ theme }) => theme.spacing.inlineLarge};
`;

const StyledSearchInput = styled(SearchInput)`
  width: 100%;
`;

const StyledStepWizardActionContainer = styled(StepWizardActionContainer)`
  padding-top: ${({ theme }) => theme.spacing.inlineLarge};
  border-top: 1px solid ${({ theme }) => theme.colors.gray300};
`;
